// src/pages/productos/VerProducto.js
import React, { useEffect, useState } from 'react'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Button from 'react-bootstrap/Button'
import { Offcanvas, Badge, Card, Spinner } from 'react-bootstrap'
import { PanelFooter, PanelHeader } from 'components/Panel'
import RichTextPreview from 'components/RichText/RichTextPreview'
import api from 'api/api'
import {
	PRODUCTOS_NO_IMAGE,
	HABILITAR_FICHA_TECNICA,
	MODULO_PROMOCIONES,
	HABILITAR_ATRIBUTOS
} from 'config/productos'

import './productos.css'
import { API_UPLOADS_URL } from 'config/general'

function VerProducto(props) {
	const { mostrar, producto, cancelar } = props
	const [productoData, setProductoData] = useState(null)

	// Estados para atributos
	const [atributosData, setAtributosData] = useState(null)
	const [loadingAtributos, setLoadingAtributos] = useState(false)

	// Funciones auxiliares para normalizar estados booleanos
	const esDestacado = (producto) => {
		return producto?.destacado === '1' || producto?.destacado === 1 || producto?.destacado === true;
	}

	const esActivo = (producto) => {
		return producto?.activo === '1' || producto?.activo === 1 || producto?.activo === true;
	}

	// Cargar atributos del producto
	const cargarAtributos = async (itemId) => {
		if (!HABILITAR_ATRIBUTOS) return

		setLoadingAtributos(true)
		try {
			const { datos } = await api.get(`a/items/${itemId}/atributos`)
			setAtributosData(datos)
		} catch (error) {
			console.error('Error al cargar atributos:', error)
			setAtributosData(null)
		} finally {
			setLoadingAtributos(false)
		}
	}

	useEffect(() => {
		if (producto && mostrar) {
			setProductoData(producto)

			// Cargar atributos
			if (HABILITAR_ATRIBUTOS && producto.id) {
				cargarAtributos(producto.id)
			}
		} else {
			setProductoData(null)
			setAtributosData(null)
		}
	}, [producto, mostrar])

	const handleClose = () => {
		setAtributosData(null)
		cancelar()
	}

	const formatearPrecio = (precio) => {
		if (!precio || precio === 0) return "-"
		return new Intl.NumberFormat('es-PY', { style: 'currency', currency: 'PYG' }).format(precio)
	}

	// Renderizar contenido usando RichTextPreview si es JSON válido
	const renderDescripcion = () => {
		if (!productoData || !productoData.descripcion) {
			return <p className="text-muted">No hay descripción disponible</p>
		}

		try {
			// Intentar parsearlo como JSON para RichTextPreview
			const contenidoJSON = JSON.parse(productoData.descripcion);
			return <RichTextPreview rawContent={contenidoJSON} />
		} catch (e) {
			// Si no es un JSON válido, mostrar como texto plano
			return <div className="contenido-text">{productoData.descripcion}</div>
		}
	}

	// Renderizar sección de atributos
	const renderAtributos = () => {
		if (!HABILITAR_ATRIBUTOS) return null

		if (loadingAtributos) {
			return (
				<Card className="mb-4 shadow-sm">
					<Card.Header className="bg-light">
						<h5 className="mb-0">
							<i className="fa fa-tags me-2"></i>
							Atributos / Variantes
						</h5>
					</Card.Header>
					<Card.Body className="text-center py-4">
						<Spinner animation="border" size="sm" className="me-2" />
						Cargando atributos...
					</Card.Body>
				</Card>
			)
		}

		if (!atributosData || !atributosData.atributos || atributosData.atributos.length === 0) {
			return (
				<Card className="mb-4 shadow-sm">
					<Card.Header className="bg-light">
						<h5 className="mb-0">
							<i className="fa fa-tags me-2"></i>
							Atributos / Variantes
						</h5>
					</Card.Header>
					<Card.Body>
						<p className="text-muted mb-0">
							<i className="fa fa-info-circle me-2"></i>
							Este producto no tiene atributos configurados.
						</p>
					</Card.Body>
				</Card>
			)
		}

		// Filtrar solo tipos que tienen valores seleccionados
		const tiposConValores = atributosData.atributos.filter(tipo => {
			const tipoIdStr = String(tipo.id)
			const valoresSeleccionados = atributosData.valoresSeleccionados[tipoIdStr] || atributosData.valoresSeleccionados[tipo.id] || []
			return valoresSeleccionados.length > 0
		})

		if (tiposConValores.length === 0) {
			return (
				<Card className="mb-4 shadow-sm">
					<Card.Header className="bg-light">
						<h5 className="mb-0">
							<i className="fa fa-tags me-2"></i>
							Atributos / Variantes
						</h5>
					</Card.Header>
					<Card.Body>
						<p className="text-muted mb-0">
							<i className="fa fa-info-circle me-2"></i>
							Este producto tiene atributos disponibles pero ninguno configurado.
						</p>
						<small className="text-muted">
							Atributos disponibles: {atributosData.atributos.map(t => t.nombre).join(', ')}
						</small>
					</Card.Body>
				</Card>
			)
		}

		return (
			<Card className="mb-4 shadow-sm">
				<Card.Header className="bg-light">
					<h5 className="mb-0">
						<i className="fa fa-tags me-2"></i>
						Atributos / Variantes
					</h5>
				</Card.Header>
				<Card.Body>
					{tiposConValores.map(tipo => {
						const tipoIdStr = String(tipo.id)
						const valoresSeleccionadosIds = atributosData.valoresSeleccionados[tipoIdStr] || atributosData.valoresSeleccionados[tipo.id] || []
						const valoresSeleccionados = tipo.valores.filter(v => valoresSeleccionadosIds.includes(v.id))

						return (
							<div key={tipo.id} className="atributo-tipo-ver mb-3 pb-3 border-bottom">
								<div className="d-flex align-items-center flex-wrap mb-2">
									<strong>{tipo.nombre}</strong>
									{tipo.heredado && (
										<Badge bg="secondary" className="ms-2" style={{ fontSize: '0.7rem' }}>
											<i className="fa fa-sitemap me-1"></i>
											Heredado
										</Badge>
									)}
									{tipo.directo && !tipo.heredado && (
										<Badge bg="info" className="ms-2" style={{ fontSize: '0.7rem' }}>
											<i className="fa fa-link me-1"></i>
											Directo
										</Badge>
									)}
									{!tipo.activo && (
										<Badge bg="warning" text="dark" className="ms-2" style={{ fontSize: '0.7rem' }}>
											Inactivo
										</Badge>
									)}
									<Badge bg="light" text="dark" className="ms-auto">
										{valoresSeleccionados.length} {valoresSeleccionados.length === 1 ? 'valor' : 'valores'}
									</Badge>
								</div>
								<div className="d-flex flex-wrap gap-2">
									{valoresSeleccionados.map(valor => (
										<Badge
											key={valor.id}
											bg={valor.activo ? "primary" : "secondary"}
											className="px-3 py-2"
										>
											{valor.nombre}
											{!valor.activo && (
												<i className="fa fa-eye-slash ms-1" title="Valor inactivo"></i>
											)}
										</Badge>
									))}
								</div>
							</div>
						)
					})}

					{/* Mostrar atributos heredados sin valores configurados */}
					{atributosData.atributos.length > tiposConValores.length && (
						<div className="mt-3 pt-2">
							<small className="text-muted">
								<i className="fa fa-info-circle me-1"></i>
								Atributos disponibles sin configurar: {' '}
								{atributosData.atributos
									.filter(t => !tiposConValores.find(tc => tc.id === t.id))
									.map(t => t.nombre)
									.join(', ')
								}
							</small>
						</div>
					)}
				</Card.Body>
			</Card>
		)
	}

	// No renderizar nada si no hay producto o no se está mostrando
	if (!productoData || !mostrar) {
		return null
	}

	// Verificar si el producto está destacado o activo
	const productoDestacado = esDestacado(productoData);
	const productoActivo = esActivo(productoData);

	return (
		<Offcanvas show={mostrar} onHide={handleClose} placement="end" className="producto-detalle-panel">
			<PanelHeader
				onClick={handleClose}
				iconClass="fa-arrow-left"
				btnText="Atrás"
				text="Detalles del producto"
			/>

			<Offcanvas.Body>
				<Row>
					<Col sm={12}>
						<div className="d-flex justify-content-between align-items-start mb-3">
							<h4 className="mb-0">
								{productoDestacado && (
									<Badge bg="warning" className="me-2 badge-destacado">
										<i className="fa fa-star"></i> Destacado
									</Badge>
								)}
								{productoData.nombre}
							</h4>
							<Badge bg={productoActivo ? "success" : "secondary"} className="estado-badge">
								{productoActivo ? "Activo" : "Inactivo"}
							</Badge>
						</div>

						{/* Imágenes del producto */}
						{productoData.multimedia && productoData.multimedia.length > 0 ? (
							<Card className="mb-4 shadow-sm">
								<div className="main-image-container">
									<img
										src={API_UPLOADS_URL + productoData.multimedia[0].url}
										alt={productoData.nombre}
										className="main-product-image"
										onError={(e) => { e.target.onerror = null; e.target.src = PRODUCTOS_NO_IMAGE }}
									/>
									{productoData.multimedia.length > 1 && (
										<div className="imagen-contador">
											<Badge bg="dark">
												<i className="fa fa-images me-1"></i> {productoData.multimedia.length} imágenes
											</Badge>
										</div>
									)}
								</div>
								{productoData.multimedia.length > 1 && (
									<div className="thumbnails-container mt-2">
										{productoData.multimedia.map((imagen, index) => (
											<div key={imagen.id} className={`thumbnail-item ${index === 0 ? 'active' : ''}`}>
												<img
													src={API_UPLOADS_URL + imagen.url}
													alt={`Imagen ${index + 1}`}
													className="thumbnail-image"
													onError={(e) => { e.target.onerror = null; e.target.src = PRODUCTOS_NO_IMAGE }}
												/>
												{index === 0 && (
													<div className="thumbnail-badge">
														<Badge bg="primary" pill>Principal</Badge>
													</div>
												)}
											</div>
										))}
									</div>
								)}
							</Card>
						) : (
							<Card className="mb-4 shadow-sm">
								<div className="no-image-container">
									<img src={PRODUCTOS_NO_IMAGE} alt="Sin imagen" className="no-product-image" />
								</div>
							</Card>
						)}

						{/* Información básica */}
						<Card className="mb-4 shadow-sm">
							<Card.Header className="bg-light">
								<h5 className="mb-0">Información básica</h5>
							</Card.Header>
							<Card.Body>
								<Row>
									<Col sm={6}>
										<dl className="row mb-0">
											<dt className="col-sm-4">Código</dt>
											<dd className="col-sm-8">{productoData.codigo || '-'}</dd>

											<dt className="col-sm-4">Marca</dt>
											<dd className="col-sm-8">{productoData.marca || '-'}</dd>

											<dt className="col-sm-4">Orden</dt>
											<dd className="col-sm-8">{productoData.orden || '0'}</dd>
										</dl>
									</Col>

									<Col sm={6}>
										<dl className="row mb-0">
											<dt className="col-sm-4">Precio</dt>
											<dd className="col-sm-8">
												{productoData.precio && parseFloat(productoData.precio) > 0 ? (
													formatearPrecio(productoData.precio)
												) : (
													'Sin precio'
												)}
											</dd>

											{MODULO_PROMOCIONES && productoData.precioPromocion && parseFloat(productoData.precioPromocion) > 0 && (
												<>
													<dt className="col-sm-4">Promoción</dt>
													<dd className="col-sm-8">
														<span className="precio-promocion">
															<Badge bg="danger" className="me-1 badge-promocion">
																<i className="fa fa-tag"></i>
															</Badge>
															{formatearPrecio(productoData.precioPromocion)}
														</span>
													</dd>
												</>
											)}
										</dl>
									</Col>
								</Row>
							</Card.Body>
						</Card>

						{/* Categorías */}
						<Card className="mb-4 shadow-sm">
							<Card.Header className="bg-light">
								<h5 className="mb-0">Categorías</h5>
							</Card.Header>
							<Card.Body>
								{productoData.jerarquias && productoData.jerarquias.length > 0 ? (
									<div className="d-flex flex-wrap gap-2">
										{productoData.jerarquias.map(categoria => (
											<Badge key={categoria.id} bg="secondary" className="px-3 py-2">
												{categoria.nombre}
											</Badge>
										))}
									</div>
								) : (
									<p className="text-muted mb-0">No hay categorías asignadas</p>
								)}
							</Card.Body>
						</Card>

						{/* Atributos / Variantes */}
						{renderAtributos()}

						{/* Descripción */}
						{productoData.descripcion && (
							<Card className="mb-4 shadow-sm">
								<Card.Header className="bg-light">
									<h5 className="mb-0">Descripción</h5>
								</Card.Header>
								<Card.Body>
									<div className="descripcion-container">
										{renderDescripcion()}
									</div>
								</Card.Body>
							</Card>
						)}

						{/* Ficha técnica */}
						{HABILITAR_FICHA_TECNICA && (
							<Card className="mb-4 shadow-sm">
								<Card.Header className="bg-light">
									<h5 className="mb-0">Ficha técnica</h5>
								</Card.Header>
								<Card.Body>
									{productoData.fichaTecnica ? (
										<a
											href={productoData.fichaTecnica.url}
											target="_blank"
											rel="noopener noreferrer"
											className="btn btn-outline-primary"
										>
											<i className="fa fa-file-pdf me-2"></i>
											Ver ficha técnica
										</a>
									) : '-'}
								</Card.Body>
							</Card>
						)}
					</Col>
				</Row>
			</Offcanvas.Body>

			<PanelFooter>
				<Button
					variant="primary"
					onClick={handleClose}
					className="w-100"
				>
					Cerrar
				</Button>
			</PanelFooter>
		</Offcanvas>
	)
}

export default VerProducto